import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { memberDisplayName, MembersTable } from './MembersTable';
import { UserAccessModal } from './UserAccessModal';
import { useAccount } from '../../contexts/AccountContext';
import { useToast } from '../../contexts/ToastContext';
import { toUserErrorMessage } from '../../lib/apiError';
import { useMembers } from '../../services/api/hooks/useMembers';
import { useUpdateMemberStatus } from '../../services/api/hooks/useUpdateMemberStatus';
import type { ExtranetUserOutput } from '../../types/extranet-api.types';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { OffsetPagination } from '../common/OffsetPagination';

const DEFAULT_LIMIT = 20;

export const MembersTab = () => {
  const { me } = useAccount();
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [editing, setEditing] = useState<ExtranetUserOutput | null>(null);
  const [statusTarget, setStatusTarget] = useState<ExtranetUserOutput | null>(null);

  const offset = Math.max(0, Number(searchParams.get('offset') ?? 0));
  const limit = Number(searchParams.get('limit') ?? DEFAULT_LIMIT);
  const search = searchParams.get('search') ?? '';

  const query = useMembers({ offset, limit });
  const updateStatus = useUpdateMemberStatus();

  // Filtre client sur la page courante : `GET /v1/users` n'a pas de recherche.
  const filteredItems = useMemo(() => {
    const items = query.data?.items ?? [];
    const needle = search.trim().toLowerCase();
    if (!needle) return items;
    return items.filter(member => {
      const name = memberDisplayName(member, member.userKey === me.userKey ? me.fullName : '');
      return name.toLowerCase().includes(needle) || (member.email?.toLowerCase().includes(needle) ?? false);
    });
  }, [query.data, search, me.userKey, me.fullName]);

  const updateParams = (mutate: (p: URLSearchParams) => void) =>
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      mutate(next);
      return next;
    });

  const confirmStatusChange = () => {
    if (!statusTarget) return;
    const nextStatus = statusTarget.status === 'ACTIVE' ? 'DISABLED' : 'ACTIVE';
    const name = memberDisplayName(statusTarget);
    updateStatus.mutate(
      { id: statusTarget.id, status: nextStatus },
      {
        onSuccess: () => {
          toast.success(
            nextStatus === 'DISABLED' ? 'Membre désactivé' : 'Membre réactivé',
            nextStatus === 'DISABLED' ? `${name} ne peut plus se connecter.` : `${name} peut de nouveau se connecter.`,
          );
          setStatusTarget(null);
        },
        onError: err => {
          const { title, description } = toUserErrorMessage(err, 'Échec du changement de statut');
          toast.error(title, description);
          setStatusTarget(null);
        },
      },
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <input
          type="text"
          value={search}
          onChange={e =>
            updateParams(p => {
              if (e.target.value) p.set('search', e.target.value);
              else p.delete('search');
            })
          }
          placeholder="Filtrer la page par nom ou email"
          aria-label="Filtrer les membres"
          className="h-8 px-3 text-sm bg-white border border-zinc-300 rounded-md text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:border-zinc-400 focus:ring-2 focus:ring-zinc-900/10 transition-colors w-72"
        />
      </div>

      <MembersTable
        items={filteredItems}
        isLoading={query.isLoading}
        isFetching={query.isFetching}
        isError={query.isError}
        error={query.error}
        currentUserKey={me.userKey}
        currentUserFullName={me.fullName}
        onEditAccess={setEditing}
        onToggleStatus={setStatusTarget}
      />

      <OffsetPagination
        offset={offset}
        limit={limit}
        pageSize={query.data?.items?.length ?? 0}
        total={query.data?.totalItems ?? 0}
        onOffsetChange={next => updateParams(p => p.set('offset', String(next)))}
        onLimitChange={next =>
          updateParams(p => {
            p.set('limit', String(next));
            p.set('offset', '0');
          })
        }
      />

      <UserAccessModal
        member={editing}
        isSelf={editing?.userKey === me.userKey}
        fallbackName={me.fullName}
        onClose={() => setEditing(null)}
      />

      <ConfirmDialog
        open={statusTarget !== null}
        title={
          statusTarget?.status === 'ACTIVE'
            ? `Désactiver ${memberDisplayName(statusTarget)} ?`
            : `Réactiver ${statusTarget ? memberDisplayName(statusTarget) : ''} ?`
        }
        description={
          statusTarget?.status === 'ACTIVE'
            ? 'Ce membre ne pourra plus se connecter au portail. Ses rôles et périmètres sont conservés.'
            : 'Ce membre pourra de nouveau se connecter avec ses rôles et périmètres actuels.'
        }
        confirmLabel={statusTarget?.status === 'ACTIVE' ? 'Désactiver' : 'Réactiver'}
        tone={statusTarget?.status === 'ACTIVE' ? 'danger' : 'default'}
        isPending={updateStatus.isPending}
        onConfirm={confirmStatusChange}
        onClose={() => setStatusTarget(null)}
      />
    </div>
  );
};
